import {DaysOfWeek} from 'common/src/modules/adScheduling/adScheduling.types';
import {
  DTScheduleNode,
  ScheduleNodeOutputData,
} from 'common/src/modules/decisionTree/decisionTree.types';
import {OutputType} from 'common/src/modules/nodes/nodes.types';
import {TimezoneSource} from 'common/src/modules/timezones/timezones.types';
import {toAiScheduleType} from '../aiModels/cardDetailsAI.enums';
import {getBaseHeader, getDestinationPhrase, getOutputIdentifier} from './utils';

const FALLBACK_WHEN_CLAUSE = 'when none of the outputs above match';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function formatMinutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

function formatDayRange(days: number[]): string {
  const sorted = [...days].sort((a, b) => a - b);

  if (sorted.length === 0) return '';
  if (sorted.length === 1) return DAY_NAMES[sorted[0]] ?? '';

  const first = DAY_NAMES[sorted[0]];
  const last = DAY_NAMES[sorted[sorted.length - 1]];

  return `${first} to ${last}`;
}

function formatDaysOfWeekEntry(entry: DaysOfWeek): string {
  const dayRange = formatDayRange(entry.days);
  const start = formatMinutesToTime(entry.start_minute);
  const end = formatMinutesToTime(entry.end_minute);

  return `${dayRange} ${start} till ${end}`;
}

function formatDaysOfWeek(daysOfWeek: DaysOfWeek[]): string {
  return daysOfWeek.map(formatDaysOfWeekEntry).join(', ');
}

export function scheduleCardToText(card: DTScheduleNode, mode: 'short' | 'full'): string {
  const header = getBaseHeader(card);

  const outputLines = card.outputs.map((output: ScheduleNodeOutputData) => {
    const outputId = getOutputIdentifier(output);
    const dest = getDestinationPhrase(output.nodeId);
    const isUnconnected = !output.nodeId;
    const isFallback = output.type === OutputType.Fallback;

    if (mode === 'short') {
      if (isFallback) {
        return `Output ${outputId} (fallback) ${dest}.`;
      }

      return `Output ${outputId} ${dest}.`;
    }

    if (isFallback) {
      const fallbackPart = isUnconnected
        ? `${dest}, active ${FALLBACK_WHEN_CLAUSE}`
        : `${dest} ${FALLBACK_WHEN_CLAUSE}`;

      return `Output ${outputId} (fallback) ${fallbackPart}.`;
    }

    if (output.dateRange) {
      const datePart = `from ${output.dateRange.start.toISO()} to ${output.dateRange.end.toISO()}`;
      const suffix = isUnconnected ? `${dest}, active ${datePart}` : `${dest} ${datePart}`;

      return `Output ${outputId} ${suffix}.`;
    }

    if (output.daysOfWeek && output.daysOfWeek.length > 0) {
      const daysPart = formatDaysOfWeek(output.daysOfWeek);
      const suffix = isUnconnected ? `${dest}, active ${daysPart}` : `${dest} ${daysPart}`;

      return `Output ${outputId} ${suffix}.`;
    }

    return `Output ${outputId} ${dest}.`;
  });

  if (mode === 'short') {
    const scheduleTypeName = toAiScheduleType(card.scheduleType) ?? '';
    const purpose = `Purpose: Select output by ${scheduleTypeName}`;

    return [header, purpose, ...outputLines].join('\n');
  }

  const timeZoneLine =
    card.timezoneSource === TimezoneSource.Manual
      ? `Time Zone: ${card.timeZone ?? 'not set'}`
      : null;
  const selectionModeLine = `Output Selection Mode: ${card.randomizeOutputs ? 'Random Eligible' : 'First Eligible'}`;

  return [header, timeZoneLine, selectionModeLine, ...outputLines].filter(Boolean).join('\n');
}
